import React, { useEffect, useState } from 'react';
import {
    CheckCircle2, Clock, ExternalLink, Eye, FileSignature, Loader2, Send, ShieldAlert, XCircle, Zap,
} from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { descreverEntregaTermo, type TermoEntrega } from '../../../src/lib/entradaAcordoRegras';
import { formatarBRL, paraCentavos } from '../../../src/lib/parcelamentoCalculo.ts';
import { acordoVigente, ehOrigemDesconto, ORIGENS_DESCONTO } from '../../../src/lib/parcelamentoRegras.ts';

/**
 * Situação e trilha de um acordo de parcelamento. Chamado #5686.
 *
 * Existe porque, com a máquina de estados, "o acordo está ativo?" deixou de ser
 * uma pergunta de uma palavra: depende de o cliente ter assinado E a entrada ter
 * caído. Sem esta peça o operador olharia "Aguardando entrada" sem saber se o
 * problema é o termo, o boleto ou o prazo.
 *
 * A trilha é `parcelamento_eventos` — append-only no banco, então o que está
 * aqui é o que aconteceu, sem reescrita possível.
 */

interface Evento {
    id: string;
    tipo: string;
    descricao: string;
    usuario_nome: string | null;
    created_at: string;
}

interface Props {
    acordoId: string;
    status: string;
    termoUrl?: string | null;
    /** Link de quem assumiu a dívida (coobrigado), quando há. */
    termoUrlCoobrigado?: string | null;
    termoNumero?: string | null;
    termoAssinadoEm?: string | null;
    /** `parcelamentos.termo_entrega`: canal do convite e se o cliente abriu (#6203). */
    termoEntrega?: TermoEntrega | null;
    entradaValor?: number | null;
    entradaVencimento?: string | null;
    entradaPagaEm?: string | null;
    /** Cobrança da entrada paga no cronograma, antes de o acordo gravar a data (#6342). */
    entradaPagaNoCronograma?: boolean;
    expiraEm?: string | null;
    dividaBruta?: number | null;
    /** Débitos fora do sistema somados à dívida (08/10/2026). Já estão dentro
     *  de `dividaBruta`; aqui só se mostra o que são. */
    outrosDebitos?: { descricao: string; valorCentavos: number }[] | null;
    /** Desconto dado sobre a dívida (R$). Zero ou ausente = sem desconto. */
    descontoDivida?: number | null;
    /** De onde veio o desconto e a explicação do analista (#6174). Acordos
     *  anteriores não têm. */
    descontoOrigem?: string | null;
    descontoDescricao?: string | null;
    jurosTotal?: number | null;
    taxaJurosMensal?: number | null;
    saldoKwh?: number | null;
    cancelamentoMotivo?: string | null;
}

const ICONE: Record<string, React.ReactNode> = {
    criado: <FileSignature size={12} />,
    termo_enviado: <FileSignature size={12} />,
    termo_assinado: <CheckCircle2 size={12} />,
    entrada_paga: <CheckCircle2 size={12} />,
    efetivado: <Zap size={12} />,
    aprovado: <CheckCircle2 size={12} />,
    cancelado: <XCircle size={12} />,
    expirado: <Clock size={12} />,
    falha: <ShieldAlert size={12} />,
    alerta: <ShieldAlert size={12} />,
    em_atraso: <Clock size={12} />,
    regularizado: <CheckCircle2 size={12} />,
    entrada_enviada: <Send size={12} />,
    entrada_emitida: <Clock size={12} />,
    termo_visualizado: <Eye size={12} />,
    link_enviado: <Send size={12} />,
};

const COR_EVENTO: Record<string, string> = {
    efetivado: 'text-emerald-600 dark:text-emerald-400',
    termo_assinado: 'text-emerald-600 dark:text-emerald-400',
    entrada_paga: 'text-emerald-600 dark:text-emerald-400',
    aprovado: 'text-emerald-600 dark:text-emerald-400',
    cancelado: 'text-red-600 dark:text-red-400',
    falha: 'text-amber-600 dark:text-amber-400',
    alerta: 'text-red-600 dark:text-red-400',
    em_atraso: 'text-orange-600 dark:text-orange-400',
    regularizado: 'text-emerald-600 dark:text-emerald-400',
    expirado: 'text-gray-500',
    entrada_enviada: 'text-emerald-600 dark:text-emerald-400',
    entrada_emitida: 'text-sky-600 dark:text-sky-400',
    termo_visualizado: 'text-sky-600 dark:text-sky-400',
    link_enviado: 'text-emerald-600 dark:text-emerald-400',
};

const fmtData = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
    return d.toLocaleDateString('pt-BR');
};

/** Uma condição de eficácia: cumprida, pendente, ou bloqueada. */
const Condicao: React.FC<{ feita: boolean; titulo: string; detalhe: string | null }> = ({
    feita, titulo, detalhe,
}) => (
    <div className="flex items-start gap-2">
        {feita
            ? <CheckCircle2 size={14} className="text-emerald-500 shrink-0 mt-0.5" />
            : <Clock size={14} className="text-amber-500 shrink-0 mt-0.5" />}
        <div className="min-w-0">
            <p className={`text-xs font-semibold ${feita ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
                {titulo}
            </p>
            {detalhe ? <p className="whitespace-pre-line text-[11px] text-gray-500 dark:text-gray-400">{detalhe}</p> : null}
        </div>
    </div>
);

const AcordoTrilhaPanel: React.FC<Props> = (p) => {
    const [eventos, setEventos] = useState<Evento[]>([]);
    const [carregando, setCarregando] = useState(true);

    useEffect(() => {
        let cancelado = false;
        (async () => {
            setCarregando(true);
            const { data } = await supabase
                .from('parcelamento_eventos')
                .select('id, tipo, descricao, usuario_nome, created_at')
                .eq('parcelamento_id', p.acordoId)
                .order('created_at', { ascending: false })
                .limit(30);
            if (cancelado) return;
            setEventos((data || []) as Evento[]);
            setCarregando(false);
        })();
        return () => { cancelado = true; };
    }, [p.acordoId]);

    const vigente = acordoVigente(p.status);
    const assinado = !!p.termoAssinadoEm;
    const entradaPaga = !!p.entradaPagaEm || !!p.entradaPagaNoCronograma;
    const pendente = ['aguardando_aprovacao', 'aguardando_assinatura', 'aguardando_entrada'].includes(p.status);

    return (
        <div className="space-y-4">
            {/* ------------------------------------------- condições de eficácia */}
            {pendente ? (
                <div className="rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/30 p-3 space-y-2">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                        Este acordo ainda não vale
                    </p>
                    <p className="text-[11px] text-amber-800 dark:text-amber-300 leading-relaxed">
                        As faturas originais seguem vencidas e com boleto válido. Elas só viram "Parcelado"
                        quando as duas condições abaixo forem cumpridas.
                    </p>
                    <div className="space-y-1.5 pt-1">
                        <Condicao
                            feita={assinado}
                            titulo={assinado ? 'Termo assinado pelo cliente' : 'Aguardando assinatura do cliente'}
                            detalhe={assinado
                                ? `Assinado em ${fmtData(p.termoAssinadoEm)}`
                                : (p.termoUrl
                                    // Uma linha por parte: cada descrição já tem " · " dentro, e juntar tudo com o mesmo
                                    // separador não deixava claro onde terminava o titular e começava o responsável.
                                    ? ([
                                        (() => {
                                            const titular = descreverEntregaTermo(p.termoEntrega?.devedor);
                                            return titular && p.termoEntrega?.coobrigado ? `Titular: ${titular}` : titular;
                                        })(),
                                        p.termoEntrega?.coobrigado ? `Responsável: ${descreverEntregaTermo(p.termoEntrega.coobrigado)}` : '',
                                    ].filter(Boolean).join('\n') || 'Termo enviado')
                                    : 'Termo ainda não foi gerado')}
                        />
                        <Condicao
                            feita={entradaPaga}
                            titulo={entradaPaga ? 'Entrada paga' : 'Aguardando pagamento da entrada'}
                            detalhe={
                                entradaPaga
                                    ? (p.entradaPagaEm ? `Compensada em ${fmtData(p.entradaPagaEm)}` : 'Boleto da entrada pago')
                                    : p.entradaValor
                                        ? `${formatarBRL(paraCentavos(Number(p.entradaValor)))} · vence ${fmtData(p.entradaVencimento)}`
                                        : 'Cobrança da entrada não gerada'
                            }
                        />
                    </div>
                    {p.expiraEm && entradaPaga ? (
                        // #6342: com a entrada paga o acordo não expira — só falta a assinatura.
                        <p className="text-[11px] text-amber-700 dark:text-amber-400 pt-1 border-t border-amber-200 dark:border-amber-900">
                            A entrada já foi paga, então a proposta <strong>não expira</strong>: o acordo passa a valer
                            assim que o cliente assinar o termo.
                        </p>
                    ) : p.expiraEm ? (
                        <p className="text-[11px] text-amber-700 dark:text-amber-400 pt-1 border-t border-amber-200 dark:border-amber-900">
                            A proposta expira em <strong>{fmtData(p.expiraEm)}</strong> (nunca antes de a entrada
                            ter tempo de compensar). Depois disso a cobrança da entrada é cancelada, o boleto dela
                            vai para baixa no Sicoob e as faturas originais seguem como estão.
                        </p>
                    ) : null}
                </div>
            ) : null}

            {p.status === 'em_atraso' ? (
                <div className="rounded-xl border border-orange-200 dark:border-orange-900 bg-orange-50/60 dark:bg-orange-950/30 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-orange-700 dark:text-orange-400 mb-1">
                        Parcela vencida em aberto
                    </p>
                    <p className="text-[11px] text-orange-800 dark:text-orange-300">
                        O sistema só sinaliza. Declarar o vencimento antecipado do acordo é decisão da gestão,
                        com notificação prévia ao cliente.
                    </p>
                </div>
            ) : null}

            {vigente ? (
                <div className="rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/30 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400 mb-1">
                        {p.status === 'concluido' ? 'Acordo concluído' : 'Acordo em vigor'}
                    </p>
                    <p className="text-[11px] text-emerald-800 dark:text-emerald-300">
                        {p.termoAssinadoEm || p.entradaPagaEm
                            ? `Assinado em ${fmtData(p.termoAssinadoEm) ?? '—'} · entrada paga em ${fmtData(p.entradaPagaEm) ?? '—'}. `
                            : 'Acordo anterior ao termo digital. '}
                        As faturas originais estão marcadas como "Parcelado".
                    </p>
                </div>
            ) : null}

            {p.cancelamentoMotivo ? (
                <div className="rounded-xl border border-red-200 dark:border-red-900 bg-red-50/60 dark:bg-red-950/30 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-red-700 dark:text-red-400 mb-1">
                        Motivo do cancelamento
                    </p>
                    <p className="text-[11px] text-red-800 dark:text-red-300">{p.cancelamentoMotivo}</p>
                </div>
            ) : null}

            {/* -------------------------------------------- condições financeiras */}
            {p.dividaBruta ? (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-1 text-[11px]">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">Condições</p>
                    <div className="flex justify-between">
                        <span className="text-gray-500">Dívida confessada</span>
                        <span className="tabular-nums font-semibold text-gray-800 dark:text-gray-200">
                            {formatarBRL(paraCentavos(Number(p.dividaBruta)))}
                        </span>
                    </div>
                    {p.outrosDebitos?.length ? (
                        <div className="pl-2 space-y-0.5">
                            {p.outrosDebitos.map((d, i) => (
                                <div key={i} className="flex justify-between gap-2 text-[10px] text-gray-400 dark:text-gray-500">
                                    <span className="truncate">inclui {d.descricao}</span>
                                    <span className="tabular-nums shrink-0">{formatarBRL(d.valorCentavos)}</span>
                                </div>
                            ))}
                        </div>
                    ) : null}
                    {Number(p.descontoDivida) > 0 ? (
                        <div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">
                                    {ehOrigemDesconto(p.descontoOrigem) ? ORIGENS_DESCONTO[p.descontoOrigem].linha : 'Desconto concedido'}
                                </span>
                                <span className="tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">
                                    − {formatarBRL(paraCentavos(Number(p.descontoDivida)))}
                                </span>
                            </div>
                            {p.descontoDescricao ? (
                                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{p.descontoDescricao}</p>
                            ) : null}
                        </div>
                    ) : null}
                    {p.entradaValor ? (
                        <div className="flex justify-between">
                            <span className="text-gray-500">Entrada</span>
                            <span className="tabular-nums font-semibold text-gray-800 dark:text-gray-200">
                                {formatarBRL(paraCentavos(Number(p.entradaValor)))}
                            </span>
                        </div>
                    ) : null}
                    <div className="flex justify-between">
                        <span className="text-gray-500">Juros do financiamento</span>
                        <span className="tabular-nums font-semibold text-amber-600 dark:text-amber-400">
                            {formatarBRL(paraCentavos(Number(p.jurosTotal || 0)))}
                            {p.taxaJurosMensal ? ` (${String(p.taxaJurosMensal).replace('.', ',')}% a.m.)` : ''}
                        </span>
                    </div>
                    {p.saldoKwh ? (
                        <div className="flex justify-between pt-1 border-t border-gray-100 dark:border-gray-800">
                            <span className="text-gray-500 flex items-center gap-1">
                                <Zap size={10} className="text-secondary" /> Saldo negociado
                            </span>
                            <span className="tabular-nums font-semibold text-gray-800 dark:text-gray-200">
                                {Number(p.saldoKwh).toLocaleString('pt-BR')} kWh
                            </span>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {/* --------------------------------------------------------- o termo */}
            {p.termoUrl ? (
                <a
                    href={p.termoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary hover:bg-primary/5 transition-colors text-xs font-semibold text-gray-700 dark:text-gray-300"
                >
                    <FileSignature size={14} className="text-primary shrink-0" />
                    <span className="flex-1 truncate">
                        Termo {p.termoNumero || ''} {assinado ? '(assinado)' : '(aguardando assinatura)'}
                    </span>
                    <ExternalLink size={12} className="text-gray-400 shrink-0" />
                </a>
            ) : null}
            {p.termoUrlCoobrigado ? (
                <a
                    href={p.termoUrlCoobrigado}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary hover:bg-primary/5 transition-colors text-xs font-semibold text-gray-700 dark:text-gray-300"
                >
                    <FileSignature size={14} className="text-primary shrink-0" />
                    <span className="flex-1 truncate">Link do responsável pela dívida</span>
                    <ExternalLink size={12} className="text-gray-400 shrink-0" />
                </a>
            ) : null}

            {/* --------------------------------------------------------- trilha */}
            <div>
                <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-2">
                    Trilha do acordo
                    <span className="ml-1.5 font-normal normal-case tracking-normal">— registro permanente</span>
                </p>
                {carregando ? (
                    <div className="flex items-center gap-2 text-[11px] text-gray-400 py-2">
                        <Loader2 size={12} className="animate-spin" /> Carregando…
                    </div>
                ) : eventos.length === 0 ? (
                    <p className="text-[11px] text-gray-400 italic py-1">
                        Sem eventos registrados (acordo anterior à trilha).
                    </p>
                ) : (
                    <ol className="space-y-2.5 max-h-56 overflow-y-auto pr-1">
                        {eventos.map((e) => (
                            <li key={e.id} className="flex gap-2">
                                <span className={`mt-0.5 shrink-0 ${COR_EVENTO[e.tipo] || 'text-gray-400'}`}>
                                    {ICONE[e.tipo] ?? <Clock size={12} />}
                                </span>
                                <div className="min-w-0">
                                    <p className="text-[11px] text-gray-700 dark:text-gray-300 leading-snug">
                                        {e.descricao}
                                    </p>
                                    <p className="text-[10px] text-gray-400">
                                        {new Date(e.created_at).toLocaleString('pt-BR')}
                                        {e.usuario_nome ? ` · ${e.usuario_nome}` : ''}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ol>
                )}
            </div>
        </div>
    );
};

export default AcordoTrilhaPanel;
